import { useEffect, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';
import { fetchProfiles } from 'flow/actions/profile-actions';
import Loading from 'flow/components/ui-components/Loading';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { routes } from 'flow/types/routes';
import { canEditProfiles, incomingCallsVaries } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from '../Page';
import SettingSection from '../SettingSection';
import Settings from './components/Settings';
import { ExtensionKey } from 'flow/types/EntityKeys';

const IncomingCalls = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const { extensionKey } = useParams<{ extensionKey: ExtensionKey }>();
    const { profiles, sortedProfiles } = useProfiles(extensionKey);

    const { isSinglePaneMode } = useSinglePaneMode();
    const { goToSettings } = useSettingsNavigation();

    const [editPerProfile, setPerProfile] = useState(
        incomingCallsVaries(profiles),
    );

    useEffect(() => {
        setPerProfile(incomingCallsVaries(profiles));
    }, [profiles]);

    useEffect(() => {
        dispatch(fetchProfiles(extensionKey));
    }, []);

    if (!profiles?.size) {
        return <Loading signalFinishedLoading={true} />;
    }

    const toggleProfileHandler = (value: boolean) => {
        if (incomingCallsVaries(profiles) && !value) {
            openModal('incomingCall', { extensionKey });
        } else {
            setPerProfile(value);
        }
    };

    const footer = canEditProfiles(profiles) ? (
        <>
            <p>{i18next.t('settings.profile_edit')}</p>
            <Toggle
                value={editPerProfile}
                onChange={toggleProfileHandler}
                text={i18next.t('settings.profile_edit')}
            />
        </>
    ) : null;

    return (
        <Page
            header="settings.incoming_calls"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
            footer={footer}
        >
            <div className="flex flex-col gap-6 divide-y">
                <div className="pb-6">
                    <SettingSection>
                        {i18next.t(sortedProfiles.first().get('description'))}
                    </SettingSection>

                    <Settings profile={sortedProfiles.first()} />
                </div>

                <div>
                    <SettingSection>
                        {i18next.t('settings.incoming_calls.other')}
                    </SettingSection>

                    <p
                        className="px-2 pb-2 text-xs text-gray-500"
                        dangerouslySetInnerHTML={{
                            __html: i18next.t(
                                editPerProfile
                                    ? 'settings.incoming_calls.help.mass_edit'
                                    : 'settings.incoming_calls.help.edit_by_profile',
                                {
                                    edit_by_profile:
                                        i18next.t('settings.profile'),
                                },
                            ),
                        }}
                    />

                    {editPerProfile ? (
                        sortedProfiles
                            .filter((profile) => profile.get('plusId') !== 0)
                            .map((profile) => (
                                <RouteLink
                                    key={profile.get('key')}
                                    to={generatePath(
                                        routes[
                                            'settings.incoming-calls.profileKey'
                                        ],
                                        {
                                            extensionKey,
                                            profileKey: profile.get('key'),
                                        },
                                    )}
                                    className="py-4"
                                >
                                    {profile.get('description')}
                                </RouteLink>
                            ))
                    ) : (
                        <Settings
                            allProfileKey={'profile'}
                            profile={sortedProfiles.skip(1).first()}
                        />
                    )}
                </div>
            </div>
        </Page>
    );
};

export default IncomingCalls;
